import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Box,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CustomTextField from "../Fields/Textfield";
import { CheckOutlined, CloseOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import BDrawer from "../BDrawer";
import { InfoCircle } from "iconsax-react";

export interface RenameDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (newName: string) => void;
  currentName: string;
  title?: string;
  label?: string;
  confirmText?: string;
  cancelText?: string;
}

const RenameDialog: React.FC<RenameDialogProps> = ({
  open,
  onClose,
  onConfirm,
  currentName,
  title,
  label,
  confirmText,
  cancelText,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [newName, setNewName] = useState(currentName);
  const resolvedTitle = title ?? t("components.historyDrawer.renameDialogTitle");
  const resolvedLabel = label ?? t("components.historyDrawer.compareNameLabel");
  const resolvedConfirmText = confirmText ?? t("components.historyDrawer.renameAction");
  const resolvedCancelText = cancelText ?? t("common.cancel");

  useEffect(() => {
    if (open) {
      setNewName(currentName);
    }
  }, [open, currentName]);

  const handleConfirm = () => {
    if (newName.trim()) {
      onConfirm(newName.trim());
    }
  };

  const handleClose = () => {
    setNewName(currentName);
    onClose();
  };

  // Action buttons component (reused in both dialog and drawer)
  const actionButtons = (
    <>
      <Button
        onClick={handleClose}
        startIcon={<CloseOutlined color="error" />}
        color="error"
        fullWidth
        variant="outlined"
      >
        {cancelText}
      </Button>
      <Button
        onClick={handleConfirm}
        disabled={!newName.trim()}
        fullWidth
        startIcon={<CheckOutlined color="inherit" />}
        color="inherit"
        variant="outlined"
      >
        {confirmText}
      </Button>
    </>
  );

  // Bottom drawer for smaller screens
  if (isMobile) {
    return (
      <BDrawer
        open={open}
        onClose={handleClose}
        anchor="bottom"
        title={title}
        showTopBar={true}
        showBottomBar={false}
        dialogActions={
          <Box sx={{ display: "flex", gap: 1, width: "100%", px: 1 }}>
            {actionButtons}
          </Box>
        }
      >
        <Box sx={{ pt: 2 }}>
          <CustomTextField
            mode="light"
            autoFocus
            label={label}
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </Box>
      </BDrawer>
    );
  }

  // Dialog for larger screens
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={0.5} >
          <InfoCircle variant="Bulk" size={24} color={theme.palette.primary.main}/>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {title}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent >
      <CustomTextField
            mode="light"
            autoFocus
            fullWidth
            label={label}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {actionButtons}
      </DialogActions>
    </Dialog>
  );
};

export default RenameDialog;

